import React, { useState, useRef } from 'react';
import { gql } from '@apollo/client';
import { useClickOutside, Breakpoint, SlidingPane } from '@moda/om';
import { compact } from '@moda/portal-stanchions';
import { SizeSelectorFragment, SizeInventoryFragment } from '../../generated/types';
import { getActiveVariantBy } from '../../lib/getActiveVariantBy';
import { SIZE_GUIDE_FRAGMENT } from '../SizeGuide';
import { SizeSelectorButton } from './SizeSelectorButton';
import { SizeSelectorList } from './SizeSelectorList';
import { MobileSelectSizeView } from './MobileSelectSizeView';
import {
  BACK_IN_STOCK_NOTIFICATON_FORM_FRAGMENT,
  BACK_IN_STOCK_NOTIFICATON_FORM_INVENTORY_FRAGMENT,
  BackInStockNotificationForm
} from './BackInStockNotificationForm';
import { BackInStockNotificationModal } from './BackInStockNotificationModal';
import './SizeSelector.scss';

export const SIZE_INVENTORY_FRAGMENT = gql`
  fragment SizeInventoryFragment on Inventory {
    sku
    size
    available
    onHand: on_hand
    onOrder: on_order
    canSell: can_sell
    processDeliveryDates: process_delivery_dates {
      deliveryStartsOn: delivery_starts_on
      deliveryEndsOn: delivery_ends_on
    }
    ...SizeGuideFragment
    ...BackInStockNotificationFormInventoryFragment
  }
  ${SIZE_GUIDE_FRAGMENT}
  ${BACK_IN_STOCK_NOTIFICATON_FORM_INVENTORY_FRAGMENT}
`;

export const VARIANT_INVENTORY_FRAGMENT = gql`
  fragment VariantInventoryFragment on VariantData {
    color
    inventory {
      ...SizeInventoryFragment
    }
  }
  ${SIZE_INVENTORY_FRAGMENT}
`;

export const SIZE_SELECTOR_FRAGMENT = gql`
  fragment SizeSelectorFragment on Variant {
    sizeEquivalents: size_equivalents
    fitRecommendation: fit_recommendation {
      description
    }
    otherVariants: other_variants_data {
      ...VariantInventoryFragment
    }
    masterVariant: master_variants_data {
      ...VariantInventoryFragment
    }
    ...BackInStockNotificationFormFragment
  }
  ${VARIANT_INVENTORY_FRAGMENT}
  ${BACK_IN_STOCK_NOTIFICATON_FORM_FRAGMENT}
`;

type Props = {
  activeVariantId: string;
  variant: SizeSelectorFragment;
  selected: SizeInventoryFragment | null;
  onSelect(option: SizeInventoryFragment): void;
};

enum Mode {
  Resting,
  SelectSize,
  NotifyMe
}

export const SizeSelector: React.FC<Props> = ({ activeVariantId, variant, selected, onSelect }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState(Mode.Resting);
  const [notifyMeSize, setNotifyMeSize] = useState<SizeInventoryFragment>();

  const activeVariant = getActiveVariantBy(variant, 'id', activeVariantId);

  useClickOutside(ref, () => (mode === Mode.SelectSize ? setMode(Mode.Resting) : null));

  const handleSelect = (size: SizeInventoryFragment) => {
    if (size.canSell) {
      onSelect(size);
      setMode(Mode.Resting);
    } else {
      setMode(Mode.NotifyMe);
      setNotifyMeSize(size);
    }
  };

  return (
    <div ref={ref} className="SizeSelector">
      <SizeSelectorButton
        data-test-id="size-selector"
        open={mode !== Mode.Resting}
        selectedSize={selected}
        onClick={() => setMode(mode === Mode.Resting ? Mode.SelectSize : Mode.Resting)}
      />

      <Breakpoint gt="md">
        {mode === Mode.SelectSize && (
          <SizeSelectorList
            className="SizeSelector__dropdown"
            sizes={compact(activeVariant.inventory)}
            sizeEquivalents={variant.sizeEquivalents}
            selectedSize={selected}
            onSelect={handleSelect}
          />
        )}

        <BackInStockNotificationModal
          show={mode === Mode.NotifyMe}
          activeVariantId={activeVariantId}
          variant={variant}
          size={notifyMeSize}
          onClose={() => setMode(Mode.Resting)}
        />
      </Breakpoint>

      <Breakpoint lt="md">
        <SlidingPane
          title="Select a size"
          visible={mode === Mode.SelectSize}
          onClose={() => setMode(Mode.Resting)}
        >
          <MobileSelectSizeView
            fitRecommendation={variant.fitRecommendation?.description}
            sizes={compact(activeVariant.inventory)}
            sizeEquivalents={variant.sizeEquivalents}
            selectedSize={selected}
            onSelect={handleSelect}
          />
        </SlidingPane>

        <SlidingPane
          title="Notify Me"
          visible={mode === Mode.NotifyMe}
          onClose={() => setMode(Mode.Resting)}
        >
          <BackInStockNotificationForm
            activeVariantId={activeVariantId}
            variant={variant}
            size={notifyMeSize}
            onClose={() => setMode(Mode.Resting)}
          />
        </SlidingPane>
      </Breakpoint>
    </div>
  );
};
